/* ---------------------------------------------------------------------------
   SFD shelving bay configurator

   Companion to sections/sfd-shelving-config.liquid. Read the header comment in
   that file first: it explains why this page inherits the shelter stylesheet
   rather than carrying its own look, and why the add goes through Easify.

   WHAT THIS DOES THAT THE OLD PAGE DID NOT

   1. Answers "which width, and how many" rather than only "how many of the
      width you happen to have picked". Every width the product is made in gets
      a row with the bays that fit and the gap left over, and the rows are
      clickable. Where a mix of two widths fills the wall better than any
      single width can, it says so.
   2. Draws the run, because the leg rule has never landed in words. A bay
      carries one upright. Four bays make four uprights and an open end, so a
      run needs one finisher to close it.
   3. Suggests the leg end finisher in Easify instead of waiting to be asked,
      then sets its quantity from the number of runs.
   4. Shows the quantity discount in money per bay rather than as a percentage,
      read out of Easify's own table so the figures cannot drift.

   THE RULE ABOUT DRIVING EASIFY

   Click its controls. Never set the value of its inputs. Easify ignores
   untrusted input events on its text fields, proven twice: on the colour
   swatches and on the cycle rack configuration box. It does honour clicks on
   its own buttons and on its quantity stepper.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var root = document.querySelector('[data-shv]');
  if (!root) return;

  var S = {};
  try { S = JSON.parse(root.querySelector('[data-shv-state]').textContent); }
  catch (e) { return; }

  var QUOTE_URL = root.getAttribute('data-quote-url') || '';
  var AUTO_FIN = root.getAttribute('data-auto-finisher') === '1';
  var DRAWER_NOTE = 'sfd_open_drawer_at';

  /* ---------------- small helpers ---------------- */

  /* Money and VAT, the house way.

     Every price held here is ex VAT pence, the way Shopify stores it. The
     store has an EX VAT / INC VAT switch in the header which sets a cookie and
     puts vat-ex or vat-inc on <html>, then fires sfd:vat on the document. The
     ordinary theme price swaps with CSS; a page that repaints, like this one,
     has to do it in the script and repaint on the event. */
  var VAT_RATE = (parseFloat(root.getAttribute('data-vat-rate')) || 20) / 100;

  function money(pence) {
    return '£' + (pence / 100).toLocaleString('en-GB',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function vatInc() { return document.documentElement.classList.contains('vat-inc'); }
  function headline(p) { return money(vatInc() ? Math.round(p * (1 + VAT_RATE)) : p); }
  function secondary(p) {
    return vatInc() ? money(p) + ' ex VAT'
                    : money(Math.round(p * (1 + VAT_RATE))) + ' inc VAT';
  }
  /* Lee, 30 Oct: "we always need to say if the price is inc / ex vat depending
     on the toggle for the site." A number on its own is not a price when the
     store shows both. */
  function vatWord() { return vatInc() ? 'inc VAT' : 'ex VAT'; }
  function headlineL(p) { return headline(p) + ' ' + vatWord(); }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] == null ? '' : map[k];
    });
  }
  function mmTxt(n) { return Number(n || 0).toLocaleString('en-GB') + 'mm'; }
  function el(sel) { return root.querySelector(sel); }
  function all(sel) { return [].slice.call(root.querySelectorAll(sel)); }
  function esc(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Option value for a role, 1 based to match Shopify's option1/2/3. */
  function valOf(v, role) {
    var i = S.roles[role];
    if (!i) return null;
    return i === 1 ? v.o1 : (i === 2 ? v.o2 : v.o3);
  }

  /* "500mm" -> 500. "1250 mm" -> 1250. */
  function mmOf(label) {
    var m = String(label || '').match(/(\d[\d,]*)\s*mm/i);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) : 0;
  }

  /* "4 x Upper Shelves (370mm)" -> { n: 4, depth: 370 } */
  function shelfOf(label) {
    var t = String(label || '');
    var n = t.match(/^\s*(\d+)\s*(?:x\s*)?/i);
    var d = t.match(/\((\d+)\s*mm\)/i);
    return { n: n ? parseInt(n[1], 10) : 0, depth: d ? parseInt(d[1], 10) : 0 };
  }

  function uniq(list) {
    var seen = {}, out = [];
    list.forEach(function (x) {
      var k = String(x);
      if (x != null && x !== '' && !seen[k]) { seen[k] = 1; out.push(x); }
    });
    return out;
  }

  /* ---------------- state ---------------- */

  var state = {
    mode: 'bays',
    bays: 1,
    wall: 1000,
    runs: 1,
    qty: 1,          /* used only by the "I know what I need" mode */
    width: null,
    shelf: null,
    colour: null,
    /* The run as a list of parts, set by picking a suggestion. Null means the
       ordinary one width run. Cleared whenever the wall, the runs or the width
       change, because a plan built for one wall is not a plan for another. */
    plan: null
  };

  var lastFinisherSet = null;   /* the number we last put in Easify's box */
  var finisherTouched = false;  /* true once we have suggested it, or they have */
  var builtSteps = false;

  function widths() {
    return uniq(S.variants.map(function (v) { return valOf(v, 'width'); }))
      .sort(function (a, b) { return mmOf(a) - mmOf(b); });
  }
  function shelves() {
    return uniq(S.variants.map(function (v) { return valOf(v, 'shelf'); }));
  }
  function colours() {
    return uniq(S.variants.map(function (v) { return valOf(v, 'colour'); }));
  }

  function current() {
    for (var i = 0; i < S.variants.length; i++) {
      var v = S.variants[i];
      if (S.roles.width && valOf(v, 'width') !== state.width) continue;
      if (S.roles.shelf && valOf(v, 'shelf') !== state.shelf) continue;
      if (S.roles.colour && valOf(v, 'colour') !== state.colour) continue;
      return v;
    }
    return null;
  }

  /* Is this combination made at all? Used to grey an option rather than let
     somebody pick their way into a variant that does not exist. */
  function made(role, value) {
    for (var i = 0; i < S.variants.length; i++) {
      var v = S.variants[i];
      if (valOf(v, role) !== value) continue;
      var ok = true;
      ['width', 'shelf', 'colour'].forEach(function (r) {
        if (r === role || !S.roles[r] || state[r] == null) return;
        if (valOf(v, r) !== state[r]) ok = false;
      });
      if (ok) return true;
    }
    return false;
  }

  /* ---------------- the run ---------------- */

  function wallMm() { return Math.max(0, parseInt(state.wall, 10) || 0); }
  function runCount() { return Math.max(1, parseInt(state.runs, 10) || 1); }

  /* Ways of filling the wall, best first.

     THE FAULT THIS REPLACES. The first version ranked by leftover alone and
     then only spoke up if a mix beat the best single width. On an 8,500mm wall
     that made 17 x 500mm the winner, because 500 divides 8,500 exactly, and it
     said nothing else. Nobody builds seventeen narrow bays when six 1250s and
     a 1000 does the same wall. Leftover is not the only thing that matters:
     the number of bays matters just as much, and these are suggestions, not an
     order, so there should be more than one of them.

     So: enumerate every combination of ONE OR TWO widths, which is where the
     sensible answers live, and rank by leftover, then by how few bays it
     takes. Two widths covers it because a wall wants a long run of one size
     with a smaller bay making up the end. Three or more is a cutting list.

     Cheap enough to do on every keystroke: five widths is twenty five pairs,
     each walked over at most a few dozen counts. */
  function fitSuggestions() {
    var wall = wallMm();
    var ws = widths().filter(function (x) { return mmOf(x) > 0; });
    if (!wall || !ws.length) return [];

    var found = [];

    function add(parts, gap) {
      var bays = 0;
      parts.forEach(function (p) { bays += p.n; });
      if (!bays || bays > 60) return;
      /* The leading width is the one most of the run is made of. It is what
         the suggestion is really proposing, and it is what gets selected if
         somebody clicks the row. */
      var lead = parts.slice().sort(function (a, b) {
        return (b.n - a.n) || (mmOf(b.w) - mmOf(a.w));
      })[0].w;
      found.push({ parts: parts, gap: gap, bays: bays, lead: lead });
    }

    ws.forEach(function (a) {
      var wa = mmOf(a);
      var maxA = Math.floor(wall / wa);
      if (maxA < 1) return;

      /* one width on its own */
      add([{ w: a, n: maxA }], wall - (maxA * wa));

      /* that width, with a second making up the end */
      ws.forEach(function (b) {
        if (b === a) return;
        var wb = mmOf(b);
        for (var n = maxA; n >= 1; n--) {
          var rest = wall - (n * wa);
          if (rest < wb) continue;
          var m = Math.floor(rest / wb);
          if (m < 1) continue;
          add([{ w: a, n: n }, { w: b, n: m }], rest - (m * wb));
        }
      });
    });

    if (!found.length) return [];

    found.sort(function (x, y) { return (x.gap - y.gap) || (x.bays - y.bays); });

    /* One suggestion per leading width, so the list reads as real
       alternatives rather than three near identical answers. */
    var seen = {}, out = [];
    found.forEach(function (f) {
      if (seen[f.lead]) return;
      seen[f.lead] = 1;
      out.push(f);
    });

    /* Never offer something materially worse than the best. A quarter of a
       narrow bay of extra waste is the limit. */
    var narrow = mmOf(ws[0]) || 500;
    var cutoff = out[0].gap + Math.round(narrow / 4);
    return out.filter(function (f) { return f.gap <= cutoff; }).slice(0, 3);
  }

  /* A stable fingerprint for a list of parts, so a plan can be matched against
     a suggestion without caring what order they came out in. */
  function sig(parts) {
    return parts.slice()
      .sort(function (a, b) { return mmOf(a.w) - mmOf(b.w); })
      .map(function (p) { return p.n + '@' + p.w; })
      .join('|');
  }

  function suggestionText(f) {
    return f.parts
      .slice()
      .sort(function (a, b) { return mmOf(b.w) - mmOf(a.w); })
      .map(function (p) { return p.n + ' x ' + esc(p.w); })
      .join(' + ');
  }

  /* Bays in one run of the chosen width. */
  function baysPerRun() {
    if (state.plan && state.plan.length) {
      var s = 0;
      state.plan.forEach(function (p) { s += p.n; });
      return s;
    }
    if (state.mode === 'wall') {
      var w = mmOf(state.width);
      return w ? Math.floor(wallMm() / w) : 0;
    }
    var n = Math.max(0, parseInt(state.bays, 10) || 0);
    var r = runCount();
    return r > 0 ? Math.floor(n / r) : n;
  }

  function bayCount() {
    /* The third way of ordering has no runs and no wall: the number in the
       box by the price is the whole order. */
    if (state.mode === 'know') return Math.max(0, parseInt(state.qty, 10) || 0);
    if (state.plan && state.plan.length) return baysPerRun() * runCount();
    if (state.mode === 'wall') return baysPerRun() * runCount();
    return Math.max(0, parseInt(state.bays, 10) || 0);
  }

  /* What actually goes in the basket, as whole quantities rather than per run.

     THE FAULT THIS FIXES. Picking the suggestion "8 x 1000mm + 1 x 665mm" set
     the width to 1000mm and nothing else, so Add to basket put in eight 1000s
     and the leg finisher and quietly dropped the 665. Lee, 29 Oct. A page that
     works the run out for you and then orders two thirds of it is worse than
     one that never offered. The run is a list of parts now, not a single
     width, and every part goes in. */
  function addParts() {
    var runs = runCount();
    if (state.plan && state.plan.length) {
      return state.plan.map(function (p) { return { w: p.w, n: p.n * runs }; });
    }
    return state.width ? [{ w: state.width, n: bayCount() }] : [];
  }

  /* The variant for a width, holding the shelf count and colour already
     chosen. Returns null where that combination is not made. */
  function variantFor(width) {
    for (var i = 0; i < S.variants.length; i++) {
      var v = S.variants[i];
      if (S.roles.width && valOf(v, 'width') !== width) continue;
      if (S.roles.shelf && valOf(v, 'shelf') !== state.shelf) continue;
      if (S.roles.colour && valOf(v, 'colour') !== state.colour) continue;
      return v;
    }
    return null;
  }

  /* How many bays land in each run.

     Not simply bays divided by runs. Twenty six bays over four runs is six
     and a half, and the diagram used to draw four runs of six while the buy
     box sold twenty six. A page whose whole job is doing the arithmetic
     cannot be out by two bays in its own picture, so the remainder is spread
     across the earlier runs: 7, 7, 6, 6. */
  function runLengths() {
    var runs = runCount();
    var out = [], i;
    if (state.mode === 'wall') {
      var per = baysPerRun();
      for (i = 0; i < runs; i++) out.push(per);
      return out;
    }
    var total = bayCount();
    var base = Math.floor(total / runs);
    var extra = total - (base * runs);
    for (i = 0; i < runs; i++) out.push(base + (i < extra ? 1 : 0));
    return out;
  }

  function finisherCount() {
    var per = parseInt(S.perRun, 10);
    if (isNaN(per)) per = 1;
    return Math.max(0, per * runCount());
  }

  /* ---------------- the option steps, in house markup ---------------- */

  function optBtn(role, value, inner, isMade) {
    return '<button type="button" class="shc-opt" data-shv-pick="' + role + '"' +
           ' data-shv-val="' + esc(value) + '"' +
           ' aria-pressed="' + (state[role] === value ? 'true' : 'false') + '"' +
           (isMade ? '' : ' data-made="0"') + '>' + inner + '</button>';
  }

  function stepBlock(no, title, gridCls, inner) {
    return '<div class="shv-step">' +
           '<span class="shc-lab"><span class="shc-no">' + no + '</span>' + esc(title) + '</span>' +
           '<div class="shc-opts shv-opts ' + gridCls + '">' + inner + '</div></div>';
  }

  function buildSteps() {
    var host = el('[data-shv-steps]');
    if (!host || builtSteps) return;

    var out = '', no = 0;

    if (S.roles.width) {
      no++;
      var ws = widths();
      out += stepBlock(no, S.labels.width, ws.length > 4 ? 'shv-g5' : 'shv-g4',
        ws.map(function (x) {
          return optBtn('width', x, '<b>' + esc(x) + '</b>', made('width', x));
        }).join(''));
    }

    if (S.roles.shelf) {
      no++;
      var sh = shelves();
      out += stepBlock(no, S.labels.shelf, sh.length > 3 ? 'shv-g4' : 'shv-g3',
        sh.map(function (x) {
          var p = shelfOf(x);
          var lab = '<b>' + esc(p.n ? (p.n + ' shelves') : x) + '</b>';
          if (p.depth) lab += '<span>' + p.depth + 'mm deep</span>';
          return optBtn('shelf', x, lab, made('shelf', x));
        }).join(''));
    }

    if (S.roles.colour) {
      no++;
      var cs = colours();
      /* Lee, 29 Oct: the colours go on one line. Four of them, so four across,
         and the swatch is the house bar rather than a round chip. */
      out += stepBlock(no, S.labels.colour, 'shv-g' + Math.min(4, Math.max(2, cs.length)),
        cs.map(function (x) {
          var hex = (S.swatches || {})[x] || '';
          var sw = '<span class="shc-sw"' + (hex ? ' style="background:' + esc(hex) + '"' : '') + '></span>';
          /* "Black Sand (RAL 7021)" is a name and a reference, not one label.
             The name is the choice, the RAL number is how a specifier checks
             it, so it goes underneath in the smaller type the house option
             tile already has. */
          var m = String(x).match(/^(.*?)\s*\(([^)]+)\)\s*$/);
          var lab = m
            ? '<b>' + esc(m[1]) + '</b><span>' + esc(m[2]) + '</span>'
            : '<b>' + esc(x) + '</b>';
          return optBtn('colour', x, sw + lab, made('colour', x));
        }).join(''));
    }

    host.innerHTML = out;
    builtSteps = true;
  }

  function paintSteps() {
    all('[data-shv-pick]').forEach(function (b) {
      var role = b.getAttribute('data-shv-pick');
      var val = b.getAttribute('data-shv-val');
      b.setAttribute('aria-pressed', state[role] === val ? 'true' : 'false');
      if (made(role, val)) b.removeAttribute('data-made');
      else b.setAttribute('data-made', '0');
    });
  }

  /* ---------------- the wall fit table ---------------- */

  function paintFit() {
    var wrap = el('[data-shv-fitwrap]');
    var tbl = el('[data-shv-fit]');
    var note = el('[data-shv-fitnote]');
    if (!wrap || !tbl) return;

    if (state.mode !== 'wall' || !wallMm()) { wrap.hidden = true; return; }

    var subs = fitSuggestions();
    if (!subs.length) {
      tbl.innerHTML = '';
      if (note) {
        var smallest = widths()[0];
        note.innerHTML = smallest
          ? 'That wall is narrower than the smallest bay we make, which is <b>' + esc(smallest) + '</b>.'
          : '';
      }
      wrap.hidden = false;
      return;
    }

    /* Tag what each row is actually best at, rather than labelling the first
       one and hoping. The list is sorted by leftover, so the top row is the
       closest fit, but a row further down can genuinely take fewer bays. */
    var minGap = Math.min.apply(null, subs.map(function (f) { return f.gap; }));
    var minBays = Math.min.apply(null, subs.map(function (f) { return f.bays; }));
    var taggedFit = false, taggedBays = false;

    var html = '<thead><tr><th>Suggestion</th><th>Bays</th><th>Left over</th></tr></thead><tbody>';
    subs.forEach(function (f) {
      /* A row is current when it is the plan being priced. With no plan, fall
         back to matching the width, so the table and the choice above it still
         agree when somebody picks a width by hand. */
      var on = state.plan ? (sig(state.plan) === sig(f.parts)) : (f.lead === state.width);
      var tag = '';
      if (!taggedFit && f.gap === minGap && subs.length > 1) {
        tag = ' <em>closest fit</em>';
        taggedFit = true;
      } else if (!taggedBays && f.bays === minBays && subs.length > 1) {
        tag = ' <em>fewest bays</em>';
        taggedBays = true;
      }
      /* The row carries its own parts rather than an index into the list.

         An index meant the click had to recompute the suggestions and trust
         them to come back in the same order. Measured on 29 Oct: clicking the
         first row applied the second row's run. Whatever caused the drift, a
         row that carries what it says cannot drift at all. */
      /* data-shv-rowparts, NOT data-shv-parts. The derived parts panel in the
         buy box already owns that name, so every suggestion row was answering
         to it and paint() was hiding and showing a table row while the real
         panel did whatever it liked. That is why "Worked out from your run"
         kept disappearing, and why it was mostly on multiple widths: the rows
         only exist once there are suggestions to show. */
      html += '<tr data-shv-fitrow data-shv-rowparts="' + esc(JSON.stringify(f.parts)) + '"' +
              ' data-shv-val="' + esc(f.lead) + '"' +
              ' aria-current="' + (on ? 'true' : 'false') + '">' +
              '<td>' + suggestionText(f) + tag + '</td>' +
              '<td>' + f.bays + '</td>' +
              '<td>' + (f.gap === 0 ? 'none' : mmTxt(f.gap)) + '</td></tr>';
    });
    html += '</tbody>';
    tbl.innerHTML = html;

    if (note) {
      note.innerHTML = 'Suggestions, not a fixed order: build the run however suits the wall.';
    }
    wrap.hidden = false;
  }

  /* ---------------- the run diagram ---------------- */

  function paintDiagram() {
    var wrap = el('[data-shv-diawrap]');
    var host = el('[data-shv-dia]');
    if (!wrap || !host) return;

    if (state.mode === 'know') { wrap.hidden = true; return; }

    /* Drop empty runs. Somebody who asks for three bays across five runs has
       mistyped, and drawing two runs holding nothing does not help them. */
    var lens = runLengths().filter(function (n) { return n > 0; });
    var runs = lens.length;
    if (!runs || lens[0] < 1) { wrap.hidden = true; return; }

    var ROWS = Math.min(runs, 3);
    var html = '';

    for (var r = 0; r < ROWS; r++) {
      var per = lens[r];
      var SHOW = Math.min(per, 10);
      html += '<div class="shv-dia__run"><span class="shv-dia__lbl">Run ' + (r + 1) + '</span>';
      for (var b = 0; b < SHOW; b++) {
        html += '<span class="shv-dia__leg"></span><span class="shv-dia__bay"></span>';
      }
      if (per > SHOW) html += '<span class="shv-dia__more">+' + (per - SHOW) + '</span>';
      html += '<span class="shv-dia__leg shv-dia__leg--end"></span>';
      html += '<span class="shv-dia__n">' + per + (per === 1 ? ' bay' : ' bays') + '</span>';
      html += '</div>';
    }
    if (runs > ROWS) {
      var rest = lens.slice(ROWS);
      var same = rest.every(function (n) { return n === rest[0]; });
      html += '<p class="shv-dia__rest">and ' + rest.length + ' more run' +
              (rest.length === 1 ? '' : 's') +
              (same ? ' of ' + rest[0] : '') + '.</p>';
    }

    host.innerHTML = html;
    wrap.hidden = false;
  }

  /* ---------------- painting everything else ---------------- */

  function paint() {
    buildSteps();
    paintSteps();
    paintFit();
    paintDiagram();

    /* The third way of ordering puts the calculator and everything derived
       from it away, and brings out a quantity box by the price instead. */
    var know = state.mode === 'know';
    var body = el('[data-shv-runbody]');
    if (body) body.hidden = know;
    var qrow = el('[data-shv-qtyrow]');
    if (qrow) qrow.hidden = !know;
    var qin = el('[data-shv-qty]');
    if (qin && String(qin.value) !== String(state.qty)) qin.value = state.qty;

    var v = current();
    var bays = bayCount();
    var fin = finisherCount();

    /* the plain answer under the calculator */
    var ans = el('[data-shv-answer]');
    if (ans) {
      if (state.mode === 'wall' && wallMm() && state.width) {
        var w = mmOf(state.width);
        var per = w ? Math.floor(wallMm() / w) : 0;
        if (per < 1) {
          ans.hidden = true;
        } else {
          var gap = wallMm() - (per * w);
          var txt = '<b>' + per + ' x ' + esc(state.width) + '</b> fills ' + mmTxt(per * w) +
                    ' of your ' + mmTxt(wallMm()) + ' wall' +
                    (gap ? ', leaving <b>' + mmTxt(gap) + '</b>.' : ', exactly.');
          if (runCount() > 1) txt += ' Across ' + runCount() + ' runs that is <b>' + bays + ' bays</b>.';
          ans.hidden = false;
          ans.innerHTML = txt;
        }
      } else {
        ans.hidden = true;
      }
    }

    /* the derived parts panel */
    var parts = el('[data-shv-parts]');
    var pnote = el('[data-shv-partsnote]');
    if (parts && pnote) {
      /* Three reasons to keep it shut, all of them Lee's, 30 Oct: the customer
         has said no finisher, or they are in "I know what I need" where there
         is no run to work anything out from. */
      if (fin > 0 && bays > 0 && !know && !finisherOff()) {
        parts.hidden = false;
        pnote.innerHTML = 'Each bay comes with one upright. ' +
          'You have said <b>' + runCount() + (runCount() === 1 ? ' run' : ' runs') +
          '</b>, so you need <b>' + fin + ' leg end finisher' + (fin === 1 ? '' : 's') +
          '</b> to complete the ' + (runCount() === 1 ? 'run' : 'runs') + '.' +
          (AUTO_FIN ? ' We have set that above, you may not need this if your run is ' +
                      'connecting to existing shelving.' : '');
      } else {
        parts.hidden = true;
      }
    }

    /* availability */
    var stock = el('[data-shv-stock]');
    if (stock) {
      if (!v) stock.innerHTML = '';
      else if (v.av) {
        stock.innerHTML = '<p class="shc-stock shc-stock--in"><span class="shc-dot"></span>' +
          esc(S.inStock || 'In stock') + '</p>';
      } else {
        stock.innerHTML = '<p class="shc-stock shc-stock--out"><span class="shc-dot"></span>' +
          esc(S.outStock || 'Not held at the moment') + '</p>';
      }
    }

    /* the summary line and price */
    var line = el('[data-shv-line]');
    var big = el('[data-shv-price]');
    var alt = el('[data-shv-altprice]');

    if (v) {
      var bits = [];
      /* A mixed run is named by all of it. Saying "1000mm" over a run that is
         eight 1000s and a 665 is how the 665 went missing in the first place. */
      if (state.plan && state.plan.length > 1) {
        bits.push(state.plan.slice()
          .sort(function (a, b) { return mmOf(b.w) - mmOf(a.w); })
          .map(function (p) { return p.n + ' x ' + p.w; })
          .join(' + '));
      } else if (state.width) {
        bits.push(state.width);
      }
      if (state.shelf) { var sp = shelfOf(state.shelf); bits.push(sp.n ? sp.n + ' shelves' : state.shelf); }
      if (state.colour) bits.push(state.colour);
      if (bays) bits.push(bays + (bays === 1 ? ' bay' : ' bays'));
      var summary = bits.join(' · ');
      if (line) line.textContent = summary;

      var unit = v.p;
      var band = bandFor(bays);
      var unitAfter = band ? Math.round(unit * (1 - band.pct / 100)) : unit;

      /* The headline is the WHOLE RUN, not one bay.

         It used to be the unit price with "per bay at 8, was £86.91"
         underneath, which made the biggest number on the page the smallest
         thing being bought and buried the saving in a sentence. Lee, 30 Oct.
         It now matches the shelter and bollard pages: Our Price, the run
         total, the other VAT figure under it, and the saving as a gold pill.
         Both figures follow the header VAT switch together. */
      /* The headline is the sum of the breakdown, nothing else. */
      var runTotal = runBreakdown().total;
      if (big) big.textContent = headlineL(runTotal);
      if (alt) alt.textContent = secondary(runTotal);

      /* The saving pill.

         Two things can make a saving and they add up: the quantity band, which
         is Easify's tiered pricing, and a compare at price on the variant if
         one is set for a monthly offer. The WAS figure is derived, never
         looked up: it is what is on screen plus the saving, so the pill can
         never contradict the price above it. */
      var pill = el('[data-shv-promo]');
      if (pill) {
        var savePence = runSaving();
        if (savePence > 0) {
          pill.innerHTML =
            (S.promoLabel ? esc(S.promoLabel) + ' - ' : '') +
            esc(fill(S.promoSaving || 'Save {amount}', { amount: headline(savePence) })) + ' ' +
            esc(S.promoWas || 'Was') +
            ' <s>' + headline(runTotal + savePence) + '</s>';
          pill.hidden = false;
        } else {
          pill.innerHTML = '';
          pill.hidden = true;
        }
      }
      paintLines(v, bays, fin, unitAfter);
      paintSticky(summary, unitAfter, bays);
    } else {
      if (big) big.textContent = '';
      if (alt) alt.textContent = '';
      var pw = el('[data-shv-promo]');
      if (pw) { pw.innerHTML = ''; pw.hidden = true; }
      var lw = el('[data-shv-lines]');
      if (lw) lw.hidden = true;
    }

    paintBands();
    autoPickFinisher();
    syncEasifyQty();
    syncHiddenForm();
  }

  /* ONE place that works out what the run costs.

     The headline and the breakdown used to compute it separately and they
     disagreed: on a mixed run the headline priced every bay at the lead
     width's rate, so eight 1000s and a 665 came out £1,000.69 at the top
     against £979.60 in the list underneath. Lee, 30 Oct: "the calculation
     value shown on screen vs the basket is different."

     Now there is one function. The rows it returns ARE the breakdown, and
     their sum IS the headline, so the two cannot drift apart again. Each
     width is priced at its own band, because Easify's tiered pricing applies
     to a cart line and a mixed run is more than one line. */
  function runBreakdown() {
    var rows = [];
    var parts = addParts();
    var bays = bayCount();
    var v = current();

    if (parts.length > 1) {
      parts.slice()
        .sort(function (a, b) { return mmOf(b.w) - mmOf(a.w); })
        .forEach(function (p) {
          var pv = variantFor(p.w);
          if (!pv) return;
          var pb = bandFor(p.n);
          var pu = pb ? Math.round(pv.p * (1 - pb.pct / 100)) : pv.p;
          rows.push([p.w + ' bays', p.n + ' x ' + headline(pu), p.n * pu]);
        });
    } else if (v && bays > 0) {
      var band = bandFor(bays);
      var unit = band ? Math.round(v.p * (1 - band.pct / 100)) : v.p;
      rows.push(['Bays', bays + ' x ' + headline(unit), bays * unit]);
    }

    var ebays = Math.max(0, easifyBayCount());
    easifyLines().forEach(function (l) {
      if (l.addon) {
        rows.push([l.name, l.qty + ' x ' + headline(Math.round(l.pence / l.qty)), l.pence]);
      } else if (ebays > 0) {
        /* Easify folds a non add-on option into the UNIT price of the bay, so
           it bills once per bay, whatever the quantity box says. Ask for two
           wall fixings on a nine bay run and the app charges eighteen.

           Lee, 30 Oct: "Fixing have calculated at 1 per bay not the number
           entered in the box?" The charge cannot be changed from the theme:
           per bay is Easify's model for a priced option, and the only way the
           box can mean a total is for the option to become an add-on product
           like the Leg Finisher, which is a change in the app.

           What the theme can do is stop it being a surprise. The row now
           states the real count at the real unit price, so eighteen fixings
           read as eighteen rather than hiding behind a per bay label. */
        var each = Math.round(l.pence / Math.max(1, l.qty));
        var n = ebays * Math.max(1, l.qty);
        rows.push([l.name + (l.qty > 1 ? ' (' + l.qty + ' per bay)' : ' (per bay)'),
                   n + ' x ' + headline(each), ebays * l.pence]);
      }
    });

    var total = 0;
    rows.forEach(function (r) { total += r[2]; });
    return { rows: rows, total: total };
  }

  /* What the bands and any compare at price take off the run, counted the
     same way: per width, at that width's own band. */
  function runSaving() {
    var save = 0;
    var parts = addParts();
    if (parts.length > 1) {
      parts.forEach(function (p) {
        var pv = variantFor(p.w);
        if (!pv) return;
        var pb = bandFor(p.n);
        var pu = pb ? Math.round(pv.p * (1 - pb.pct / 100)) : pv.p;
        save += (pv.p - pu) * p.n;
        if (pv.cap > pv.p) save += (pv.cap - pv.p) * p.n;
      });
      return save;
    }
    var v = current();
    var bays = bayCount();
    if (!v || bays < 1) return 0;
    var band = bandFor(bays);
    var unit = band ? Math.round(v.p * (1 - band.pct / 100)) : v.p;
    save += (v.p - unit) * bays;
    if (v.cap > v.p) save += (v.cap - v.p) * bays;
    return save;
  }

  function paintLines(v, bays, fin, unitAfter) {
    var wrap = el('[data-shv-lines]');
    if (!wrap) return;
    if (!bays) { wrap.hidden = true; return; }

    /* The rows come from runBreakdown(), the same call the headline is the
       sum of, so the two are the same arithmetic by construction rather than
       by agreement. Every figure goes through headline(), so the box is in the
       same money as the price above it. */
    var bd = runBreakdown();
    var rows = bd.rows;
    var total = bd.total;
    if (!rows.length) { wrap.hidden = true; return; }

    var html = '';
    rows.forEach(function (r) {
      html += '<div class="shc-tot"><span>' + r[0] + '</span><span>' + r[1] + '</span></div>';
    });
    html += '<div class="shc-tot shv-tot--sum"><span>Total</span><span>' +
            headlineL(total) + '</span></div>';
    wrap.innerHTML = html;
    wrap.hidden = false;
  }

  /* The sticky bar carries both VAT figures, labelled, the way the store's own
     bar does. It was showing one bare number with nothing to say which it was,
     which is the one place on the page where that matters most: the bar is
     what somebody sees when the price itself has scrolled away. The house bar
     deliberately ignores the EX / INC switch and always shows the pair. */
  function paintSticky(summary, unitAfter, bays) {
    var l = el('[data-shv-sticky-line]');
    var p = el('[data-shv-sprice]');
    if (l) l.textContent = summary;
    if (!p) return;
    var ex = unitAfter * Math.max(1, bays);
    var inc = Math.round(ex * (1 + VAT_RATE));
    p.innerHTML =
      '<div><span class="sticky-price--ex">' + money(ex) +
      '</span><span class="sticky-price--ex-label">ex VAT</span></div>' +
      '<div><span class="sticky-price--inc">' + money(inc) +
      '</span><span class="sticky-price--inc-label">inc VAT</span></div>';
  }

  /* ---------------- quantity bands, read from Easify ---------------- */

  var bands = null;

  /* Easify renders its own tiered price table. Rather than copy the numbers
     into theme settings, where they would drift the first time somebody edits
     the app, read them back out and show them in money. */
  function readBands() {
    if (bands) return bands;
    var tables = [].slice.call(document.querySelectorAll('table'));
    for (var i = 0; i < tables.length; i++) {
      var t = tables[i].textContent || '';
      if (!/save/i.test(t) || !/%/.test(t)) continue;
      var found = [];
      [].slice.call(tables[i].querySelectorAll('tr')).forEach(function (tr) {
        var txt = (tr.textContent || '').replace(/\s+/g, ' ').trim();
        var m = txt.match(/(\d+)\s*(?:-|to|–)\s*(\d+)[^%]*?([\d.]+)\s*%/i);
        if (m) { found.push({ from: +m[1], to: +m[2], pct: parseFloat(m[3]) }); return; }
        var m2 = txt.match(/from\s*(\d+)[^%]*?([\d.]+)\s*%/i);
        if (m2) found.push({ from: +m2[1], to: Infinity, pct: parseFloat(m2[2]) });
      });
      if (found.length) { bands = found; return bands; }
    }
    return null;
  }

  function bandFor(qty) {
    var b = readBands();
    if (!b || !qty) return null;
    for (var i = 0; i < b.length; i++) {
      if (qty >= b[i].from && qty <= b[i].to) return b[i];
    }
    return null;
  }

  function paintBands() {
    var b = readBands();
    var wrap = el('[data-shv-bandwrap]');
    var tbl = el('[data-shv-bands]');
    if (!wrap || !tbl) return;
    if (!b || !b.length) { wrap.hidden = true; return; }

    var v = current();
    if (!v) { wrap.hidden = true; return; }

    var active = bandFor(bayCount());

    var html = '<thead><tr><th>Bays</th><th>Price each ' + vatWord() +
               '</th><th>You save</th></tr></thead><tbody>';
    html += '<tr data-shv-bandrow aria-current="' + (active ? 'false' : 'true') + '">' +
            '<td>1 to ' + (b[0].from - 1) + '</td><td>' + headline(v.p) + '</td><td>&mdash;</td></tr>';
    b.forEach(function (x) {
      var each = Math.round(v.p * (1 - x.pct / 100));
      var label = x.to === Infinity ? (x.from + ' or more') : (x.from + ' to ' + x.to);
      var is = active && active.from === x.from;
      html += '<tr data-shv-bandrow aria-current="' + (is ? 'true' : 'false') + '">' +
              '<td>' + label + '</td><td>' + headline(each) + '</td>' +
              '<td>' + headline(v.p - each) + ' a bay</td></tr>';
    });
    html += '</tbody>';
    tbl.innerHTML = html;
    wrap.hidden = false;
  }

  /* ---------------- Easify ---------------- */

  function easifySlot() { return el('[data-shv-easify]'); }

  function easifyContainers() {
    var slot = easifySlot();
    return slot ? [].slice.call(slot.querySelectorAll('.tpo_option-container')) : [];
  }

  /* The leg finisher is the container whose cart property starts with an
     underscore, which is how Easify marks an add-on product. _tpo_ keys are
     its own bookkeeping and are not it. */
  function finisherBox() {
    var boxes = easifyContainers();
    for (var i = 0; i < boxes.length; i++) {
      var inp = boxes[i].querySelector('[name^="properties[_"]');
      if (inp && inp.name.indexOf('properties[_tpo_') !== 0) return boxes[i];
    }
    return null;
  }

  /* Has the customer turned the leg finisher down? Lee, 30 Oct: "When you say
     no leg end - no need to show worked out from your run." Fair: a panel
     explaining how many finishers we worked out for them is noise once they
     have said they do not want any. */
  function finisherOff() {
    var box = finisherBox();
    if (!box) return false;
    var inp = box.querySelector('input.tpo_option-input') ||
              box.querySelector('[name^="properties[_"]');
    var val = plain(inp ? inp.value : '');
    return !val || NO_VALUE.test(val);
  }

  /* Every priced Easify option, named, with what it actually adds.

     TWO KINDS, CHARGED DIFFERENTLY. Measured in the basket on 30 Oct, because
     getting this the wrong way round is a money error either way:

       - an ADD-ON PRODUCT, which Easify marks with a leading underscore on the
         cart property, becomes its own cart line at its own quantity. The leg
         finisher is one: one per run, not one per bay.
       - any other priced option is folded into the UNIT price of the bay, so
         it is charged once per bay. The wall fixings are one. Two bays with
         fixings came through at £99.67 each against a list of £86.91: the
         £12.76 is per bay, and the line was £199.34.

     Lee, 30 Oct: "when you add the wall fixings or what ever it's not
     appearing in the list." It was worse than not appearing: the fixings were
     being charged and left out of the total, so a two bay run showed £248.12
     and billed £278.75. */
  function easifyLines() {
    var out = [];
    easifyContainers().forEach(function (box) {
      if (box.classList.contains('tpo_hidden')) return;
      var hid = box.querySelector('[name^="properties["]');
      if (!hid || hid.name.indexOf('properties[_tpo_') === 0) return;
      var raw = String(hid.value || '');
      if (!raw) return;
      var m = raw.match(/\+\s*£\s*([\d,]+(?:\.\d+)?)/);
      if (!m) return;
      var pence = Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
      if (!pence) return;
      var qm = raw.match(/\|\s*(\d+)\s*$/);
      var lab = box.querySelector('.form__label');
      out.push({
        name: lab ? plain(lab.textContent).replace(/\s*\*\s*$/, '') : 'Option',
        pence: pence,
        qty: qm ? Math.max(1, parseInt(qm[1], 10)) : 1,
        addon: hid.name.indexOf('properties[_') === 0
      });
    });
    return out;
  }

  /* How many bays actually carry the Easify options. On a mixed run only the
     main width goes through Easify; the end bays are added plainly and have no
     options on them, so multiplying the per bay extras by the whole run would
     charge for fixings nobody is getting. */
  function easifyBayCount() {
    var parts = addParts();
    if (parts.length > 1) {
      return parts.slice().sort(function (a, b) { return b.n - a.n; })[0].n;
    }
    return bayCount();
  }

  function perBayExtrasPence() {
    var n = 0;
    easifyLines().forEach(function (l) { if (!l.addon) n += l.pence; });
    return n;
  }
  function addonExtrasPence() {
    var n = 0;
    easifyLines().forEach(function (l) { if (l.addon) n += l.pence; });
    return n;
  }

  function easifyFinisherPence() {
    var box = finisherBox();
    if (!box) return 0;
    var inp = box.querySelector('[name^="properties[_"]');
    var m = String(inp && inp.value || '').match(/\+\s*£\s*([\d,]+(?:\.\d+)?)/);
    if (!m) return 0;
    var total = Math.round(parseFloat(m[1].replace(/,/g, '')) * 100);
    /* Easify reports the line total for the add-on, not the unit, so divide by
       the quantity it has recorded. */
    var q = String(inp.value).match(/\|\s*(\d+)\s*$/);
    var n = q ? parseInt(q[1], 10) : 1;
    return n > 0 ? Math.round(total / n) : total;
  }

  /* ---------------- Easify: yes and no for optional extras ----------------

     Ported from sfd-shelter-config.js so this page looks like that one. Lee,
     29 Oct: "Why are the easifi options still not in our style? i know it can
     be done." Skinning the dropdown was not enough. The shelter page replaces
     the dropdown outright with a pair of house tiles, and that is what people
     recognise as ours.

     Where an Easify option offers "Not Required" and one real choice, it reads
     as a Yes and a No. Where it offers more than one real choice, No is still
     Not Required and Yes opens the choices underneath.

     Easify's own control stays on the page and keeps doing the work: these
     buttons click it. Its pricing, its conditional logic and the properties it
     writes on the cart line are untouched. If the markup is ever not the shape
     this expects the option is left alone and the ordinary dropdown shows.
     Nothing breaks, it just looks like the app again. */

  var NO_VALUE = /^(not required|none|no|n\/a|not needed|without)$/i;

  /* "Wall Leg Finisher Required [ +£32.95 ]" reads better as "Wall Leg
     Finisher Required". The price is shown separately, so it does not want
     saying twice. */
  function plain(t) {
    return String(t == null ? '' : t)
      /* Easify puts the add-on quantity on the end of its own field as "| 3",
         AFTER the price, so the price is no longer the last thing in the
         string and a trailing-bracket strip alone leaves both in place.

         This cost two real faults, both measured on 29 Oct. The finisher was
         never suggested, because "Not Required | 1" did not match the refusal
         test and so looked like a choice the customer had made. And the Yes
         tile showed as selected while Easify actually held Not Required,
         because no button label could ever equal that string. Strip the
         quantity first, then the price. */
      .replace(/\s*\|\s*\d+\s*$/, '')
      /* Strip a trailing SQUARE bracket group always: that is how Easify wraps
         a price, "[ +£3.90 ]".

         A trailing ROUND bracket only when it actually holds a price. It used
         to go too, and that was a real fault: the first wall fixing is called
         "Wall Fixings For Shelving Uprights (Basic)", so the button label came
         back without the "(Basic)" while the stored value kept it, the two
         could never match, and the tile never showed as selected. Lee, 30 Oct:
         "You still can't select the first optional wall fixing." It was
         selecting every time. It just never looked like it. */
      .replace(/\s*\[[^\]]*\]\s*$/, '')
      .replace(/\s*\(\s*\+?\s*[^)]*[£$€][^)]*\)\s*$/, '')
      .replace(/\s+/g, ' ').trim();
  }
  function btnLabel(b) {
    var e = b.querySelector('.tpo_option_type_label');
    return plain(e ? e.textContent : b.textContent);
  }

  /* Matching a tile to the value Easify has stored.

     Never compare these as plain strings. Easify does not use the same name in
     both places. The first wall fixing draws as "Wall Fixings For Shelving
     Uprights" and stores as "Wall Fixings For Shelving Uprights (Basic) [
     +£3.90 ] | 2": the "(Basic)" exists only in the stored value. String
     equality therefore never matched, no tile was ever marked pressed, and the
     option looked dead even though every click was landing. Lee, 30 Oct: "You
     still can't select the first optional wall fixing."

     So compare on a key with the price, the quantity and any trailing
     parenthetical taken off both sides, case folded. */
  function optKey(t) {
    return plain(t).replace(/\s*\([^)]*\)\s*$/, '').toLowerCase();
  }
  function sameOpt(a, b) { return optKey(a) === optKey(b); }
  function btnPrice(b) {
    var e = b.querySelector('.tpo_additional-price');
    var t = e ? e.textContent.replace(/[()]/g, '').trim() : '';
    return (t && !/^£?0(\.00)?$/.test(t)) ? t : '';
  }

  /* Bring an option's quantity up to the minimum Easify itself demands.

     On this product "Optional Wall Fixings" is flagged required and its
     quantity rule starts at two, while its stepper starts at one, so choosing
     it put the option straight into error with "Between 2-999" and the add was
     refused. Lee, 30 Oct: "the qty also needs to start at 2."

     The number is READ OUT OF EASIFY'S OWN MESSAGE rather than written into
     the theme, so a rule changed in the app is followed here with no code
     change, and a product with no such rule is left alone. Clicking the
     stepper, never writing to the box, for the usual reason: the app ignores
     untrusted input on its fields. */
  function enforceQtyMin(box) {
    if (!box) return;
    var inp = box.querySelector('.tpo_quantity-selector_input');
    var plus = box.querySelector('.tpo_quantity-selector_plus');
    if (!inp || !plus) return;

    var msg = box.querySelector('.tpo_error-message');
    var txt = msg ? (msg.textContent || '') : '';
    var m = txt.match(/between\s*(\d+)\s*(?:-|to|–)\s*(\d+)/i);
    if (!m) return;

    var min = parseInt(m[1], 10);
    var have = parseInt(inp.value, 10);
    if (isNaN(min) || isNaN(have) || have >= min) return;
    var steps = min - have;
    if (steps > 20) return;

    (function step(i) {
      if (i >= steps) return;
      try { plus.click(); } catch (e) { return; }
      setTimeout(function () { step(i + 1); }, 80);
    })(0);
  }

  var proxying = false;   /* mutes the observer while we drive the app */
  var syncers = [];       /* one re-read closure per proxied option */

  function resyncProxies() {
    syncers = syncers.filter(function (f) { return f.box && f.box.isConnected !== false; });
    syncers.forEach(function (f) { try { f(); } catch (e) { /* gone */ } });
  }

  function proxyEasify() {
    var slot = easifySlot();
    if (!slot) return;
    [].forEach.call(slot.querySelectorAll('.tpo_option-container'), proxyOne);
  }

  function proxyOne(box) {
    if (box.querySelector('[data-shv-yn]')) return;                     /* done */
    if (!box.querySelector('.tpo_option-input-wrapper-dropdown')) return; /* not a dropdown */

    var btns = [].slice.call(box.querySelectorAll('button.tpo_option-type-selection'));
    if (btns.length < 2) return;

    var noBtn = null, yes = [];
    btns.forEach(function (b) {
      if (!noBtn && NO_VALUE.test(btnLabel(b))) { noBtn = b; return; }
      yes.push(b);
    });

    /* Two shapes, not one.

       An option with a "Not Required" value is a yes and a no, which is what
       the shelter page handles. An option WITHOUT one is not optional at all,
       it is a straight choice: Epos Colour is eight colours and no refusal.
       The shelter page leaves those as a dropdown because none of its options
       are that shape. Lee, 29 Oct: "The epos colour should be a colour chart?"
       So lay the whole set out as tiles instead, with Easify's own picture on
       each where it has one. */
    var chart = !noBtn;
    if (chart) {
      if (btns.length > 12) return;   /* too many to lay out, leave the dropdown */
      yes = btns;
    } else if (!yes.length) {
      return;
    }

    /* Easify carries an image per value on some options, which is what makes a
       chart a chart. It has them on Wall Fixings and the Leg End Finisher. It
       has none on Epos Colour yet, so that one renders as named tiles until
       the pictures are added in the app, at which point this picks them up
       with no change here. */
    function tile(b, i) {
      var img = b.querySelector('img');
      var src = img && img.getAttribute('src');
      var pic = src
        ? '<span class="shv-pic"><img src="' + esc(src) + '" alt="" loading="lazy"></span>'
        : '';
      return '<button type="button" class="shc-opt' + (pic ? ' shv-opt--pic' : '') +
        '" data-shv-ynpick-i="' + i + '" aria-pressed="false">' + pic +
        '<b>' + esc(btnLabel(b)) + '</b>' +
        (btnPrice(b) ? '<em>+' + esc(btnPrice(b).replace(/^\+/, '')) + '</em>' : '') +
        '</button>';
    }

    var n = yes.length;
    var cols = n > 3 ? 'shv-g4' : n === 3 ? 'shv-g3' : 'shv-g2';
    var tiles = yes.map(tile).join('');

    var ui = document.createElement('div');
    ui.className = 'shc-yn shv-yn';
    ui.setAttribute('data-shv-yn', '');

    if (chart) {
      ui.innerHTML = '<div class="shc-opts shv-opts ' + cols + ' shv-chart" data-shv-ynpick>' +
        tiles + '</div>';
    } else {
      var one = n === 1;
      var yesNote = one
        ? '<span>' + esc(btnLabel(yes[0])) + '</span>' +
          (btnPrice(yes[0]) ? '<em>+' + esc(btnPrice(yes[0]).replace(/^\+/, '')) + '</em>' : '')
        : '<span>' + n + ' to choose from</span>';
      ui.innerHTML =
        '<div class="shc-opts shv-opts shv-g2">' +
          '<button type="button" class="shc-opt" data-shv-yn-no aria-pressed="true">' +
            '<b>No</b><span>Not required</span></button>' +
          '<button type="button" class="shc-opt" data-shv-yn-yes aria-pressed="false">' +
            '<b>Yes</b>' + yesNote + '</button>' +
        '</div>' +
        (one ? '' : '<div class="shc-ynpick shc-opts shv-opts ' + cols + '" data-shv-ynpick hidden>' +
          tiles + '</div>');
    }

    var lastYes = 0;

    function sync() {
      var inp = box.querySelector('input.tpo_option-input');
      var val = plain(inp ? inp.value : '');
      var isNo = !chart && (!val || NO_VALUE.test(val));
      /* A quantity box against an option the customer has said no to is just
         a number with nothing to count. Lee, 30 Oct: "If you say no to wall
         fixings and legends, the qty is still showing?" */
      box.classList.toggle('shv-no', isNo);
      var noT = ui.querySelector('[data-shv-yn-no]');
      var yesT = ui.querySelector('[data-shv-yn-yes]');
      if (noT) noT.setAttribute('aria-pressed', isNo ? 'true' : 'false');
      if (yesT) yesT.setAttribute('aria-pressed', isNo ? 'false' : 'true');
      var pick = ui.querySelector('[data-shv-ynpick]');
      if (!pick) return;
      if (!chart) pick.hidden = isNo;
      [].forEach.call(pick.children, function (t, i) {
        var on = !isNo && sameOpt(btnLabel(yes[i]), val);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) lastYes = i;
      });
    }

    /* Click the app's own button and let it do everything it normally does.
       Easify does not always have its own field written by the next tick, so
       read it back twice: once quickly so the page feels immediate, once later
       so the right choice ends up highlighted. */
    function hit(b) {
      if (!b) return;
      proxying = true;
      try { b.click(); } catch (e) { /* leave the dropdown as it was */ }
      setTimeout(function () { resyncProxies(); showExtras(); paint(); }, 180);
      setTimeout(function () {
        proxying = false;
        resyncProxies(); enforceQtyMin(box); showExtras(); paint();
      }, 700);
    }

    ui.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      /* Press the tile straight away rather than waiting for Easify to write
         its field and the resync to read it back. Lee, 30 Oct: "selecting them
         is slow." The sync a moment later corrects it if the app disagrees. */
      [].forEach.call(ui.querySelectorAll('button'), function (x) {
        x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
      });
      if (b.hasAttribute('data-shv-yn-no')) { hit(noBtn); return; }
      if (b.hasAttribute('data-shv-yn-yes')) {
        var yt = ui.querySelector('[data-shv-yn-yes]');
        if (yt) yt.setAttribute('aria-pressed', 'true');
        hit(yes[lastYes] || yes[0]);
        return;
      }
      var i = b.getAttribute('data-shv-ynpick-i');
      if (i != null) {
        var yt2 = ui.querySelector('[data-shv-yn-yes]');
        if (yt2) yt2.setAttribute('aria-pressed', 'true');
        lastYes = parseInt(i, 10);
        hit(yes[lastYes]);
      }
    });

    /* The dropdown is not a direct child of the option container, it sits two
       levels down inside the label block, so insert against its own parent. */
    var ref = box.querySelector('.tpo_option-input-wrapper');
    if (ref && ref.parentNode) ref.parentNode.insertBefore(ui, ref);
    else box.appendChild(ui);
    box.classList.add('shc-proxied', 'shv-proxied');
    sync.box = box;
    syncers.push(sync);
    sync();
  }

  /* Suggest the finisher rather than wait to be asked.

     Lee, 29 Oct: the page was not suggesting the leg ends at all. The option
     is required and opens blank, so the customer met a dead end on the one
     part they most often forget. This opens Easify's own dropdown and clicks
     the choice that is not the refusal, which is how the app expects to be
     driven. It runs once. If the customer then sets it back to Not Required
     that choice stands, because the flag is never cleared. */
  function autoPickFinisher() {
    if (!AUTO_FIN || finisherTouched || resuming) return;
    var box = finisherBox();
    if (!box) return;

    var hidden = box.querySelector('[name^="properties[_"]');
    if (!hidden) return;
    /* A value of "Not Required" is Easify's own default, not a decision the
       customer has made, so it must not count as one. Measured on the preview
       on 29 Oct: once this page started writing ?variant= to the URL, Easify
       began pre-filling the finisher with "Not Required", and the blunter
       "has a value" test silently stopped the page suggesting it at all. */
    if (hidden.value && !NO_VALUE.test(plain(hidden.value))) {
      finisherTouched = true;
      return;
    }

    var btns = [].slice.call(box.querySelectorAll('button.tpo_option-type-selection'));
    if (!btns.length) return;

    var want = null;
    btns.forEach(function (b) {
      if (want) return;
      if (NO_VALUE.test(btnLabel(b))) return;
      want = b;
    });
    if (!want) return;

    finisherTouched = true;
    /* Click the app's own button and nothing else. An earlier version opened
       the dropdown first and then clicked; the shelter page has never needed
       to and the extra step is one more thing to go wrong. */
    proxying = true;
    try { want.click(); } catch (e) { proxying = false; return; }
    setTimeout(function () {
      proxying = false;
      lastFinisherSet = null;
      resyncProxies();
      syncEasifyQty();
      paint();
    }, 700);
  }

  /* Set Easify's own quantity stepper by clicking it.

     Never writes to the input. Easify ignores untrusted input events on its
     text fields, so setting .value looks right on screen and is not in the
     app's state when the add happens. */
  function syncEasifyQty() {
    var box = finisherBox();
    if (!box) return;
    var inp = box.querySelector('.tpo_quantity-selector_input');
    var plus = box.querySelector('.tpo_quantity-selector_plus');
    var minus = box.querySelector('.tpo_quantity-selector_minus');
    if (!inp || !plus || !minus) return;

    var want = finisherCount();
    if (want < 1) return;

    var have = parseInt(inp.value, 10);
    if (isNaN(have)) return;
    if (have === want) { lastFinisherSet = want; return; }

    /* If the customer has moved this themselves, leave it alone. We only
       adjust a number we put there. */
    if (lastFinisherSet !== null && have !== lastFinisherSet) return;

    var btn = want > have ? plus : minus;
    var steps = Math.abs(want - have);
    if (steps > 40) return;            /* something is wrong, do not hammer it */

    (function step(i) {
      if (i >= steps) { lastFinisherSet = want; return; }
      try { btn.click(); } catch (e) { return; }
      setTimeout(function () { step(i + 1); }, 90);
    })(0);
  }

  /* Move the mounted option set into the slot. Easify caches its anchor
     lookups at start up, so the anchors must stay server rendered where they
     are. Moving a node keeps its listeners. */
  function mountEasify() {
    var slot = easifySlot();
    var host = el('[data-shv-anchors]');
    if (!slot || !host) return;
    var SEL = '.tpo_option-set-wrapper, [id^="tpo_option-set-container-"]';
    var waited = 0;

    (function watch() {
      var set = host.querySelector(SEL) || slot.querySelector(SEL);
      if (set) {
        if (set.parentElement !== slot) slot.appendChild(set);
        proxyEasify();
        showExtras();
        watchEasify();
        paint();
        slot.addEventListener('change', function () { resyncProxies(); paint(); });
        slot.addEventListener('click', function () {
          setTimeout(function () { resyncProxies(); paint(); }, 60);
        });
        return;
      }
      waited += 300;
      if (waited < 8000) { setTimeout(watch, 300); return; }
      showExtras();
    })();
  }

  function showExtras() {
    var slot = easifySlot();
    var wrap = slot && slot.closest('[data-shv-easifywrap]');
    if (!wrap || !slot) return;
    var any = easifyContainers().some(function (c) {
      return !c.classList.contains('tpo_hidden');
    });
    wrap.hidden = !any;
  }

  function watchEasify() {
    var slot = easifySlot();
    if (!slot || !window.MutationObserver) return;
    var t = null;
    /* Attributes as well as children: the app shows and hides its options by
       class, so a class change is the signal that the block may now be empty
       or may have something in it again. The proxying guard stops our own
       clicks triggering a rebuild mid flight. */
    new MutationObserver(function () {
      if (proxying) return;
      clearTimeout(t);
      t = setTimeout(function () {
        proxyEasify(); resyncProxies(); showExtras(); paint();
      }, 140);
    }).observe(slot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------- the hidden form Easify reads ---------------- */

  function cartForm() { return root.querySelector('form[id^="sfd-easify-form-"]'); }

  function syncHiddenForm() {
    var f = cartForm();
    if (!f) return;
    var v = current();
    var idIn = f.querySelector('[name="id"]');
    if (idIn && v) idIn.value = v.id;
    var qIn = f.querySelector('[name="quantity"]');
    if (qIn) qIn.value = Math.max(1, bayCount() || 1);
    if (v) setVariantUrl(v.id);
  }

  /* Easify works out which of its conditional options to show by reading
     ?variant= off the URL, from inside a MutationObserver on the whole
     document. The theme's own product page writes that parameter whenever a
     variant is picked, so this does the same. Without it Easify judges every
     condition against the first available variant, which matters here because
     the leg end finisher options are being built per height and depth. It is
     also what makes a copied URL reopen on the right configuration. */
  var lastVid = null;
  function setVariantUrl(id) {
    if (String(id) === String(lastVid)) return;
    lastVid = id;
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', id);
      window.history.replaceState({}, '', u.toString());
    } catch (e) { return; }
    /* The observer only looks when something in the document changes, so give
       it a change to see. Cheap, and gone on the next tick. */
    var ping = document.createComment('shv');
    document.body.appendChild(ping);
    setTimeout(function () { if (ping.parentNode) ping.parentNode.removeChild(ping); }, 0);
  }

  /* ---------------- add to basket ---------------- */

  function openCartDrawer() {
    var cd = document.querySelector('cart-drawer');
    if (!cd) return false;
    try {
      if (typeof cd.onCartRefresh === 'function') cd.onCartRefresh();
      if (typeof cd.show === 'function') { cd.show(); return true; }
    } catch (e) { return false; }
    return false;
  }

  function noteDrawerWanted() {
    try { sessionStorage.setItem(DRAWER_NOTE, String(Date.now())); } catch (e) {}
  }
  function clearDrawerNote() {
    try { sessionStorage.removeItem(DRAWER_NOTE); } catch (e) {}
  }
  function openDrawerIfNoted() {
    var at = null;
    try { at = sessionStorage.getItem(DRAWER_NOTE); } catch (e) { return; }
    if (!at) return;
    clearDrawerNote();
    if (Date.now() - Number(at) > 15000) return;
    var tries = 0;
    (function attempt() {
      var cd = document.querySelector('cart-drawer');
      var open = false;
      try {
        open = !!cd && (cd.hasAttribute('open') ||
               window.getComputedStyle(cd).display !== 'none');
      } catch (e) { open = false; }
      if (!open) openCartDrawer();
      if (++tries > 16) return;
      setTimeout(attempt, 250);
    })();
  }

  function cartCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return c.item_count; })
      .catch(function () { return null; });
  }

  /* ---------------- finishing a run across Easify's reload ----------------

     MEASURED, TWICE, BECAUSE THE FIRST ANSWER WAS WRONG.

     An add that Easify REFUSES leaves the page alone: that is what a blank
     required field looks like, and it was the first thing tested, which gave
     the false impression that the page never reloads. An add that Easify
     ACCEPTS reloads the page. So adding a mixed run one width at a time in a
     simple loop cannot work: the first width goes in, the page reloads, and
     the rest of the run is gone. Which is exactly the fault Lee reported: the
     8 x 1000 went in, the 1 x 665 did not.

     So what is left of the run is parked in sessionStorage, which survives the
     reload, and picked up again on the way back. The extras are turned off for
     the continuation, because they were already ordered with the first width
     and a leg finisher bought twice is a real cost to somebody.

     The guards matter more than the mechanism:
       - the parked run expires after 90 seconds, so a stale one cannot fire
         into an unrelated visit
       - the basket count is parked with it, and if it has not grown on the way
         back the run stops rather than trying again, because a silent retry is
         how a customer ends up with two of everything
       - at most four widths, and each one is marked attempted before it fires
       - anything unexpected stops and says so, leaving the rest to be added by
         hand, rather than guessing */

  var QUEUE_KEY = 'sfd_shv_run';
  var QUEUE_TTL = 90000;
  var resuming = false;

  function readQueue() {
    try {
      var raw = sessionStorage.getItem(QUEUE_KEY);
      if (!raw) return null;
      var q = JSON.parse(raw);
      if (!q || !q.at || (Date.now() - q.at) > QUEUE_TTL) { clearQueue(); return null; }
      if (!q.parts || !q.parts.length) { clearQueue(); return null; }
      return q;
    } catch (e) { return null; }
  }
  function writeQueue(q) {
    try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch (e) {}
  }
  function clearQueue() {
    try { sessionStorage.removeItem(QUEUE_KEY); } catch (e) {}
  }

  /* A plain note in the buy box, so the customer can see the run is still
     being added rather than wondering what the reload was for. */
  function runNote(msg, bad) {
    var sum = el('.shc-sum');
    if (!sum) return;
    var n = el('[data-shv-runnote]');
    if (!msg) { if (n) n.remove(); return; }
    if (!n) {
      n = document.createElement('p');
      n.setAttribute('data-shv-runnote', '');
      sum.insertBefore(n, sum.firstChild);
    }
    n.className = bad ? 'shv-runnote shv-runnote--bad' : 'shv-runnote';
    n.textContent = msg;
  }

  /* Easify's own complaint, said in full.

     Measured on 29 Oct: an add with Epos Colour left blank is refused by the
     app, which writes "This is a required option" against that field, and all
     the page said at the bottom was that it had not gone in. Two messages,
     neither of them pointing at the field. Read the app's own text, name the
     option it belongs to, and use that instead. */
  function easifyErrorText() {
    var slot = easifySlot();
    if (!slot) return '';
    var out = [];
    [].forEach.call(slot.querySelectorAll('.tpo_error-message'), function (e) {
      var r = e.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var txt = (e.textContent || '').replace(/\s+/g, ' ').trim();
      if (!txt) return;
      var box = e.closest('.tpo_option-container');
      var lab = box && box.querySelector('.form__label');
      var name = lab ? plain(lab.textContent).replace(/\s*\*\s*$/, '') : '';
      out.push(name ? name + ': ' + txt : txt);
    });
    return out.join('. ');
  }

  function firstEasifyError() {
    var slot = easifySlot();
    if (!slot) return null;
    var found = null;
    [].forEach.call(slot.querySelectorAll('.tpo_error-message'), function (e) {
      if (found) return;
      var r = e.getBoundingClientRect();
      if (r.width && r.height && (e.textContent || '').trim()) {
        found = e.closest('.tpo_option-container') || e;
      }
    });
    return found;
  }

  /* Turn off every priced extra that can be turned off.

     Used on the second and later widths of a mixed run. The extras are chosen
     once for the whole run and ride on the first line, so repeating them on
     each width would order the leg finishers, and anything else with a price,
     once per width. Only options that actually offer a refusal value can be
     turned off; any that cannot are reported, and the add stops rather than
     guessing. */
  /* What the customer has chosen in Easify right now, keyed by the cart
     property so it can be matched up again after the reload. */
  function extrasSnapshot() {
    var out = [];
    easifyContainers().forEach(function (box) {
      if (box.classList.contains('tpo_hidden')) return;
      var inp = box.querySelector('input.tpo_option-input');
      var prop = box.querySelector('[name^="properties["]');
      if (!inp || !prop) return;
      out.push({ key: prop.name, value: plain(inp.value || '') });
    });
    return out;
  }

  /* Put the options back for the second width of a run.

     Easify clears its own fields on the reload, and some of them are required,
     so the continuation add is refused until they are filled. Measured on
     30 Oct: "Epos Colour: This is a required option. Leg End Finisher: This is
     a required option."

     The rule has to be different for the two kinds:

       - an option that offers a refusal value gets the refusal. Those are the
         priced extras, the leg finisher above all, and they were already
         ordered with the first width. Charging for them once per width is a
         real cost to somebody and the whole reason this is careful.
       - an option with NO refusal value is a straight required choice, Epos
         Colour being the one here, so it is put back exactly as the customer
         left it. Only when it is free: if a no-refusal option carries a price
         we stop instead, because repeating it would charge twice and guessing
         is not ours to do.

     Anything that cannot be settled by those two rules is named and the run
     stops there, with the rest left to add by hand. */
  function restoreExtras(snapshot) {
    var problems = [];
    easifyContainers().forEach(function (box) {
      if (box.classList.contains('tpo_hidden')) return;
      var prop = box.querySelector('[name^="properties["]');
      if (!prop) return;

      var lab = box.querySelector('.form__label');
      var name = lab ? plain(lab.textContent).replace(/\s*\*\s*$/, '') : 'an option';

      var btns = [].slice.call(box.querySelectorAll('button.tpo_option-type-selection'));
      if (!btns.length) return;

      var inp = box.querySelector('input.tpo_option-input');
      var now = plain(inp ? inp.value : '');
      var required = !!box.querySelector('.tpo_option-required-indicator');

      /* Leave an optional field that is already empty completely alone.

         Measured on 30 Oct: clicking "Not Required" on Optional Wall Fixings,
         which the reload had left blank anyway, woke up the quantity rule on
         that option and Easify refused the add with "Between 2-999". An
         optional field that is empty is already saying no. Touching it to make
         it say no more loudly is how you break it. */
      if (!required && (!now || NO_VALUE.test(now))) return;

      var noBtn = null;
      btns.forEach(function (b) { if (!noBtn && NO_VALUE.test(btnLabel(b))) noBtn = b; });
      if (noBtn) {
        if (NO_VALUE.test(now)) return;      /* already refused, leave it be */
        try { noBtn.click(); } catch (e) { problems.push(name); }
        return;
      }

      var want = null;
      (snapshot || []).forEach(function (s) { if (s.key === prop.name) want = s.value; });
      var match = null;
      btns.forEach(function (b) { if (!match && want && sameOpt(btnLabel(b), want)) match = b; });
      if (!match) { problems.push(name); return; }
      if (btnPrice(match)) { problems.push(name); return; }
      try { match.click(); } catch (e) { problems.push(name); }
    });
    return problems;
  }

  function clearExtras() {
    var left = [];
    easifyContainers().forEach(function (box) {
      if (box.classList.contains('tpo_hidden')) return;
      var inp = box.querySelector('input.tpo_option-input');
      if (!inp) return;
      var val = plain(inp.value || '');
      if (!val || NO_VALUE.test(val)) return;          /* already off */
      var no = null;
      [].forEach.call(box.querySelectorAll('button.tpo_option-type-selection'), function (b) {
        if (!no && NO_VALUE.test(btnLabel(b))) no = b;
      });
      var lab = box.querySelector('.form__label');
      if (!no) { left.push(lab ? plain(lab.textContent) : 'an option'); return; }
      try { no.click(); } catch (e) { left.push(lab ? plain(lab.textContent) : 'an option'); }
    });
    return left;
  }

  function addToCart(btn) {
    var err = el('[data-shv-err]');
    if (err) err.hidden = true;

    var parts = addParts();
    if (!parts.length || !current()) {
      if (err) { err.textContent = 'Choose a width, a shelf count and a colour first.'; err.hidden = false; }
      return;
    }
    if (bayCount() < 1) {
      if (err) { err.textContent = 'Tell us how many bays you need.'; err.hidden = false; }
      return;
    }

    var missing = parts.filter(function (p) { return !variantFor(p.w); });
    if (missing.length) {
      if (err) {
        err.textContent = 'We do not make a ' + missing[0].w +
          ' bay in that shelf count and colour. Change one of them, or pick a different run.';
        err.hidden = false;
      }
      return;
    }

    var f = cartForm();
    var sub = f && (f.querySelector('.sfd-easify-ghost') || f.querySelector('[type="submit"]'));
    if (!f || !sub) {
      if (err) { err.textContent = 'Could not reach the basket. Please refresh and try again.'; err.hidden = false; }
      return;
    }

    /* Nothing goes in until every required option is answered.

       Lee, 30 Oct: "If you forget to select ticket strip colour it's not
       adding any measurements, only the first width." That is the worst sort
       of fault, a half order. The end bays go in with a plain cart post and
       the main width goes through Easify, so if Easify then refuses for a
       blank required field the customer is left holding one width of a run
       they asked for in full.

       So the required fields are checked BEFORE anything is added. This is a
       narrow test on purpose: a field marked required by the app with nothing
       in it. It is not a sweep of every message the app happens to be showing,
       which is what an earlier version did, and it turned a standing warning
       on an unrelated option into a dead end. */
    var unanswered = [];
    easifyContainers().forEach(function (box) {
      if (box.classList.contains('tpo_hidden')) return;
      if (!box.querySelector('.tpo_option-required-indicator')) return;
      var inp = box.querySelector('input.tpo_option-input');
      if (!inp || plain(inp.value || '')) return;
      var lab = box.querySelector('.form__label');
      unanswered.push(lab ? plain(lab.textContent).replace(/\s*\*\s*$/, '') : 'an option');
    });
    if (unanswered.length) {
      if (err) {
        err.textContent = (unanswered.length === 1 ? 'Choose ' : 'Choose ') +
          unanswered.join(' and ') + ' before adding to the basket. ' +
          'Nothing has been added yet.';
        err.hidden = false;
      }
      var target = easifyContainers().filter(function (box) {
        if (box.classList.contains('tpo_hidden')) return false;
        if (!box.querySelector('.tpo_option-required-indicator')) return false;
        var i2 = box.querySelector('input.tpo_option-input');
        return i2 && !plain(i2.value || '');
      })[0];
      if (target && target.scrollIntoView) {
        try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      }
      return;
    }

    var btns = all('[data-shv-add]');
    var label0 = btn.textContent;
    btns.forEach(function (b) { b.disabled = true; });

    function release() {
      btn.textContent = label0;
      btns.forEach(function (b) { b.disabled = false; });
    }

    function fail(msg, scroll) {
      clearDrawerNote();
      clearQueue();          /* never leave a half run parked behind an error */
      runNote('');
      release();
      if (err) { err.textContent = msg; err.hidden = false; }
      if (scroll) {
        var target = firstEasifyError();
        if (target && target.scrollIntoView) {
          try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
      }
    }

    /* One width at a time. The page does not reload between adds, measured on
       29 Oct, so this is an ordinary sequence rather than anything that has to
       survive a navigation. */
    function step(i) {
      if (i >= parts.length) {
        clearDrawerNote();
        clearQueue();
        runNote('');
        release();
        openCartDrawer();
        return;
      }

      var p = parts[i];
      btn.textContent = parts.length > 1
        ? 'Adding ' + (i + 1) + ' of ' + parts.length + '…'
        : 'Adding…';

      if (i > 0) {
        var stuck = clearExtras();
        if (stuck.length) {
          fail('The ' + p.w + ' bays still need adding. We could not turn off ' +
               stuck[0] + ' for them, so please add them yourself rather than ' +
               'have us order the extras twice.', true);
          return;
        }
      }

      state.width = p.w;
      paintSteps();
      var v = variantFor(p.w);
      var idIn = f.querySelector('[name="id"]');
      if (idIn && v) idIn.value = v.id;
      var qIn = f.querySelector('[name="quantity"]');
      if (qIn) qIn.value = p.n;
      if (v) setVariantUrl(v.id);

      cartCount().then(function (before) {
        /* Park what is still to come BEFORE clicking, because a successful
           click reloads the page and nothing after it runs. */
        var rest = parts.slice(i + 1);
        if (rest.length) {
          writeQueue({
            at: Date.now(),
            shelf: state.shelf,
            colour: state.colour,
            parts: rest.slice(0, 4),
            count: before,
            extras: extrasSnapshot(),
            tries: 0
          });
        } else {
          clearQueue();
        }
        var done = false, tries = 0;

        function poll() {
          if (done) return;
          tries++;
          cartCount().then(function (now) {
            if (done) return;
            if (before === null || now === null) {
              done = true;
              fail('We could not read the basket, so we have stopped rather than risk ' +
                   'adding the same bays twice. Please refresh and try again.', false);
              return;
            }
            /* It landed. Easify reloads the page when that happens, and the
               resume path picks the rest of the run up on the way back, so do
               NOT carry straight on here: the two race each other, and the
               in-page one wins just often enough to clear the parked run a
               moment before the reload needs it. That is why the 1000mm half
               of an 8,665mm wall kept vanishing. Wait, and only carry on in
               page if the reload never comes. */
            if (now > before) {
              done = true;
              clearDrawerNote();
              setTimeout(function () { step(i + 1); }, 2500);
              return;
            }
            if (tries >= 9) {
              done = true;
              var app = easifyErrorText();
              fail(app
                ? app + '. Fix that and the whole run will go in.'
                : 'That did not go into the basket. Check the options above, or call us on 0116 318 4182.',
                !!app);
              return;
            }
            setTimeout(poll, 450);
          });
        }

        noteDrawerWanted();
        sub.removeAttribute('aria-disabled');
        try { sub.click(); } catch (e) {}
        setTimeout(poll, 700);
      });
    }

    /* ---- the extra widths go in first, and not through Easify ----

       A run of two widths is nearly always a long line of one size with one
       or two narrower bays making up the end. Those end bays need no options:
       the Epos colour, the fixings and the leg finisher are chosen once for
       the whole run and ride on the main line.

       Putting them in with a plain cart post, BEFORE the Easify add, sidesteps
       the whole fight with the reload: Easify reloads the page when its add
       lands, so anything that has to happen after it is at the mercy of a
       navigation. Nothing has to happen after it any more.

       The one thing a plain post loses is the quantity band, so it is only
       used where there is no band to lose: strictly fewer bays than the first
       band begins at. Anything bigger falls back to the parked run, which is
       slower and fussier but keeps the discount. */
    var main = parts.slice().sort(function (a, b) { return b.n - a.n; })[0];
    var extras = parts.filter(function (p) { return p !== main; });
    var bandsNow = readBands();
    var firstBand = (bandsNow && bandsNow.length) ? bandsNow[0].from : Infinity;
    var safeDirect = extras.length > 0 && extras.every(function (p) {
      return p.n < firstBand && !!variantFor(p.w);
    });

    if (safeDirect) {
      btn.textContent = 'Adding…';
      var items = extras.map(function (p) {
        return { id: variantFor(p.w).id, quantity: p.n };
      });
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error('rejected');
        /* Only the main width is left, so there is nothing to park and the
           reload has nothing to interrupt. */
        parts = [main];
        clearQueue();
        step(0);
      }).catch(function () {
        fail('We could not add the ' + extras[0].w + ' bays. Nothing has been ' +
             'put in the basket, so please try again.', false);
      });
      return;
    }

    step(0);
  }

  /* Pick the run back up on the way in. Runs once, early, before anything
     touches the Easify options, so the suggestion logic stays out of its way. */
  function resumeRun() {
    var q = readQueue();
    if (!q) return;

    resuming = true;
    finisherTouched = true;          /* the extras were ordered with width one */

    if (q.shelf) state.shelf = q.shelf;
    if (q.colour) state.colour = q.colour;
    state.plan = q.parts.slice();
    state.width = q.parts[0].w;

    var p = q.parts[0];
    runNote('Adding the rest of your run: ' + p.n + ' x ' + p.w + '.');

    var waited = 0;
    (function wait() {
      var f = cartForm();
      var sub = f && f.querySelector('.sfd-easify-ghost');
      var mounted = easifyContainers().length > 0;
      if (f && sub && mounted) { go(f, sub); return; }
      waited += 300;
      if (waited < 10000) { setTimeout(wait, 300); return; }
      stop('We could not reach the options to add the last ' + p.n + ' x ' + p.w +
           '. Please add them yourself.');
    })();

    function stop(msg) {
      resuming = false;
      clearQueue();
      runNote(msg, true);
      paint();
    }

    function go(f, sub) {
      cartCount().then(function (now) {
        /* Did the width before this one actually land? If not, something went
           wrong and trying again is how a basket ends up with two of
           everything. Stop and say so. */
        if (now === null || typeof q.count !== 'number' || now <= q.count) {
          stop('The ' + p.n + ' x ' + p.w + ' still needs adding: the previous ' +
               'part did not reach the basket. Please add it yourself.');
          return;
        }

        var stuck = restoreExtras(q.extras);
        if (stuck.length) {
          stop('The ' + p.n + ' x ' + p.w + ' still needs adding. We could not ' +
               'set ' + stuck[0] + ' for it without risking charging you twice, ' +
               'so please add it yourself.');
          return;
        }

        var v = variantFor(p.w);
        if (!v) { stop('We do not make a ' + p.w + ' bay in that shelf count and colour.'); return; }

        setTimeout(function () {
          /* No pre-flight on Easify's messages.

             This used to refuse to even try if the app had any error showing,
             which sounded careful and was not. Measured on 30 Oct: Optional
             Wall Fixings carries "Between 2-999" on this product from the
             moment the page loads, because its quantity rule starts at two and
             its stepper starts at one. It does not stop the first add and it
             does not stop this one. Judging the add before making it turned a
             standing warning into a dead end.

             So make the add and judge it on what happens. The watchdog below
             reports the app's own words if it really is refused. */
          var idIn = f.querySelector('[name="id"]');
          if (idIn) idIn.value = v.id;
          var qIn = f.querySelector('[name="quantity"]');
          if (qIn) qIn.value = p.n;

          var rest = q.parts.slice(1);
          if (rest.length) {
            writeQueue({
              at: Date.now(), shelf: state.shelf, colour: state.colour,
              parts: rest, count: now, extras: q.extras, tries: (q.tries || 0) + 1
            });
          } else {
            clearQueue();
            noteDrawerWanted();
          }

          sub.removeAttribute('aria-disabled');
          try { sub.click(); } catch (e) {}

          /* If this one is refused the page will not reload, so watch for that
             and say which option stopped it. */
          setTimeout(function () {
            cartCount().then(function (after) {
              if (after !== null && after > now) return;   /* landed, reload due */
              var app = easifyErrorText();
              stop(app
                ? app + '. The last ' + p.n + ' x ' + p.w + ' is still to add.'
                : 'The last ' + p.n + ' x ' + p.w + ' did not go in. Please add it yourself.');
            });
          }, 4000);
        }, 600);
      });
    }
  }

  function quote() {
    var v = current();
    if (!v) return;
    if (typeof window.addToQuote !== 'function') {
      if (QUOTE_URL) window.location.href = QUOTE_URL;
      return;
    }
    var bays = Math.max(1, bayCount());
    var band = bandFor(bays);
    var unit = band ? Math.round(v.p * (1 - band.pct / 100)) : v.p;
    var bits = [state.width, state.shelf, state.colour].filter(Boolean).join(' / ');
    window.addToQuote(v.id, bays, S.title, bits, unit, S.img, S.url, null);
  }

  /* ---------------- the sticky bar ---------------- */

  /* Show the bar once the real Add button has scrolled off the top.

     This was an IntersectionObserver first and it never fired a useful entry:
     measured on the preview, the button sat 1,700px above the viewport and the
     bar stayed hidden the whole way down the page. A scroll listener reading
     the button's own position is duller and it is right every time. */
  function wireSticky() {
    var bar = el('[data-shv-sticky]');
    var anchor = el('[data-shv-main-add]');
    if (!bar || !anchor) return;

    var queued = false;
    function check() {
      queued = false;
      bar.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0);
    }
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      (window.requestAnimationFrame || setTimeout)(check);
    }, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  /* ---------------- events ---------------- */

  root.addEventListener('click', function (e) {
    var mode = e.target.closest('[data-shv-mode]');
    if (mode) {
      state.mode = mode.getAttribute('data-shv-mode');
      /* A plan built from a wall length is not a plan for a bay count, and
         leaving it behind froze the run diagram and the finisher quantity at
         whatever the other mode had worked out. Lee, 30 Oct. */
      state.plan = null;
      lastFinisherSet = null;
      all('[data-shv-mode]').forEach(function (b) {
        b.setAttribute('aria-pressed', b === mode ? 'true' : 'false');
      });
      var fb = el('[data-shv-f-bays]'), fw = el('[data-shv-f-wall]');
      if (fb) fb.hidden = state.mode !== 'bays';
      if (fw) fw.hidden = state.mode !== 'wall';
      paint();
      return;
    }

    var fitrow = e.target.closest('[data-shv-fitrow]');
    if (fitrow) {
      /* Picking a suggestion takes the WHOLE suggestion, every width in it,
         not just the one most of it is made of. */
      var parts = null;
      try { parts = JSON.parse(fitrow.getAttribute('data-shv-rowparts') || 'null'); }
      catch (e) { parts = null; }
      if (parts && parts.length) {
        state.plan = parts.map(function (p) { return { w: p.w, n: p.n }; });
      } else {
        state.plan = null;
      }
      state.width = fitrow.getAttribute('data-shv-val');
      paint();
      return;
    }

    var pick = e.target.closest('[data-shv-pick]');
    if (pick) {
      var role = pick.getAttribute('data-shv-pick');
      /* Choosing a width by hand drops the mixed plan: they have said they
         want one width. Shelf count and colour apply to every part, so those
         leave the plan alone. */
      if (role === 'width') state.plan = null;
      state[role] = pick.getAttribute('data-shv-val');
      paint();
      return;
    }

    var step = e.target.closest('[data-shv-step]');
    if (step) {
      var by = parseInt(step.getAttribute('data-shv-step'), 10) || 0;
      state.qty = Math.max(1, (parseInt(state.qty, 10) || 1) + by);
      paint();
      return;
    }

    var add = e.target.closest('[data-shv-add]');
    if (add) { addToCart(add); return; }

    var q = e.target.closest('[data-shv-quote]');
    if (q) { quote(); return; }
  });

  root.addEventListener('input', function (e) {
    var t = e.target;
    if (t.hasAttribute('data-shv-qty')) { state.qty = t.value; paint(); return; }
    if (t.hasAttribute('data-shv-bays')) { state.bays = t.value; state.plan = null; paint(); return; }
    if (t.hasAttribute('data-shv-wall')) { state.wall = t.value; state.plan = null; paint(); return; }
    if (t.hasAttribute('data-shv-runs')) {
      state.runs = Math.max(1, parseInt(t.value, 10) || 1);
      /* The customer changed the runs, so the finisher number we suggested is
         no longer the one they are looking at. Allow it to be driven again. */
      lastFinisherSet = null;
      paint();
    }
  });

  /* ---------------- start ---------------- */

  (function seed() {
    /* Open on the variant the URL names, if it names one.

       The page writes ?variant= as the customer configures, so a copied link
       has to come back to the same bay. It also stops a stale parameter left
       in the address bar disagreeing with what the page shows: arriving with a
       six shelf variant in the URL and seeding four shelves put the wrong
       price on screen, which was how this was found. */
    var want = null;
    try {
      var q = new URL(window.location.href).searchParams.get('variant');
      if (q) {
        want = S.variants.filter(function (x) { return String(x.id) === String(q); })[0] || null;
      }
    } catch (e) { want = null; }

    var av = S.variants.filter(function (v) { return v.av; });
    var v = want || av[0] || S.variants[0];
    if (!v) return;
    if (S.roles.width) state.width = valOf(v, 'width');
    if (S.roles.shelf) state.shelf = valOf(v, 'shelf');
    if (S.roles.colour) state.colour = valOf(v, 'colour');
  })();

  paint();
  mountEasify();
  wireSticky();
  resumeRun();
  openDrawerIfNoted();

  /* The header EX VAT / INC VAT switch. Repaint so every figure on the page
     follows it together, the pill included. */
  document.addEventListener('sfd:vat', paint);
})();
